import React, { useEffect, useState } from "react";
import strings from "localisation/strings";
import theme from "style/theme";
import sheetIcon from "common/assets/icons/sheet.svg";
import gallerieIcon from "common/assets/icons/gallerie.svg";
import {
  businessTypeOptions,
  citiesOptions,
  defaultSelectLabel,
  detailCategoryStatusOptions,
  screenTargetOptions,
  typeOptions,
} from "components/table/DataTable/constants";
import BlockTitle from "components/generic/BlockTitle";
import InlineInputGroup from "components/forms/FormFields/InlineInputGroup";
import InlineNumberInput, {
  InlineInputProps,
} from "components/forms/FormFields/InlineNumberInput";
import SimpleIconedInput, {
  SingleInputFieldProps,
} from "components/forms/FormFields/SimpleIconedInput";
import SelectComponent, {
  SelectItem,
  SelectPropsInterface,
} from "components/forms/Select";
import Checkbox, { CheckboxProps } from "components/forms/Checkbox";
import {
  MultipleFieldFormProps,
  QuestionInputFieldProps,
} from "components/forms/CategoryMultipleAnswerForm";
import { ResponsiveSpreadRow } from "components/rows/SpreadRow";
import ResponsiveHalfRowColumn from "components/columns/HalfRowColumn";
import { MarginedArea } from "components/generic/ViewContainer";
import { AutoDragAndDrop } from "components/forms/FormFields";
import EntityTypes from "api/models/EntityTypes";
import ImageUploadType from "api/models/ImageUploadType";
import { getSmartServiceGroups } from "api/resources";
import useQuery from "common/hooks/useQuery";
import SmartServiceGroupInterface from "api/models/SmartServiceGroupInterface";
import styled from "styled-components";
import ScreenTarget from "api/models/ScreenTarget";
import MapsSelectList from "components/forms/FormFields/MapsSelectList";
import { TextFieldProps } from "components/forms/TextField";
import InAppLinkInput from "components/forms/FormFields/InAppLinkInput";
import { Ratio } from "components/forms/FormFields/PictureGallery/helpers";

interface Values {
  [index: string]: any;
}

const OffsetContentDiv = styled.div`
  width: ${({ theme }) => theme.forms.iconSizeOffset};
`;

const MainCategoriesForm = ({
  isNew,
  getSelectFieldProps,
  getSingleInputFieldProps,
  getCheckboxFieldProps,
  getNumberFieldProps,
  getMainGeneralCategoryLogoPictureFieldProps,
  getTextFieldProps,
  isGeneralType,
  values,
  onChange,
  getErrors,
}: {
  isNew?: boolean;
  getSelectFieldProps: (
    field: string,
    options: SelectItem[],
  ) => SelectPropsInterface;
  getSingleInputFieldProps: (field: string) => SingleInputFieldProps;
  getCheckboxFieldProps: (field: string) => CheckboxProps;
  getQuestionInputFieldProps: (
    field: string,
    subField: string,
    index: number,
  ) => QuestionInputFieldProps;
  getMainGeneralCategoryLogoPictureFieldProps: (
    field: string,
  ) => {
    imageUrl?: string | null;
    setImageUrl?: (image: string | null) => void;
  };
  getMultipleFieldFormProps: (field: string) => MultipleFieldFormProps;
  getNumberFieldProps: (field: string) => InlineInputProps;
  isQuestionRequired?: boolean;
  isGeneralType?: boolean;
  values: Values;
  onChange: (selectedField: any, selectedValue: any) => void;
  getErrors?: (field: any) => string[];
  getTextFieldProps: (field: string, errorId?: string) => TextFieldProps;
}) => {
  const { xsmall, full } = theme.forms;
  const tagTypeSelectFieldProps = getSelectFieldProps("tagType", typeOptions());
  const businessTypeSelectFieldsProps = getSelectFieldProps(
    "businessType",
    businessTypeOptions(),
  );
  const showAsSmartServiceCheckBoxFieldProps = getCheckboxFieldProps(
    "showAsSmartService",
  );
  const showAsJobOfferCheckBoxFieldProps = getCheckboxFieldProps(
    "showAsJobOffer",
  );
  const [slideErrors, setSlideErrors] = useState<string[]>([]);

  const postSelectedAsCategory =
    tagTypeSelectFieldProps.value &&
    tagTypeSelectFieldProps.value.value === EntityTypes.posts;
  const showSmartServiceGroup = showAsSmartServiceCheckBoxFieldProps.checked;

  const { result: smartServiceGroupsResult } = useQuery<
    SmartServiceGroupInterface
  >({
    request: getSmartServiceGroups,
  });

  const getSmartServiceGroupsOptions = () => {
    return (
      smartServiceGroupsResult?.smartServiceGroups.map(option => ({
        label: strings(
          `categoriesScreen.fields.smartServiceGroup.options.${option}` ||
            option,
        ),
        value: option,
      })) || []
    );
  };

  return (
    <>
      <BlockTitle
        children={
          isNew
            ? strings("categoriesScreen.title.newMainCategory")
            : strings("categoriesScreen.title.editMainCategory")
        }
      />
      <MarginedArea>
        <ResponsiveSpreadRow>
          <ResponsiveHalfRowColumn>
            <InlineInputGroup
              icon={sheetIcon}
              placeholder={strings("categoriesScreen.fields.tagType")}
              width={xsmall}
            >
              <SelectComponent
                {...tagTypeSelectFieldProps}
                placeholder={defaultSelectLabel()}
                disabled={!isNew}
                classNamePrefix={"option"}
                small
              />
            </InlineInputGroup>
          </ResponsiveHalfRowColumn>

          <ResponsiveHalfRowColumn>
            <SimpleIconedInput
              icon={sheetIcon}
              width={xsmall}
              inputWidth={full}
              inputPlaceholder={strings(
                "categoriesScreen.fields.code.placeholder",
              )}
              {...getSingleInputFieldProps("code")}
            />
          </ResponsiveHalfRowColumn>
        </ResponsiveSpreadRow>

        <ResponsiveSpreadRow>
          {/* <ResponsiveHalfRowColumn>
            <InlineInputGroup
              icon={sheetIcon}
              placeholder={strings("categoriesScreen.fields.businessType")}
              width={xsmall}
            >
              <SelectComponent
                {...businessTypeSelectFieldsProps}
                placeholder={defaultSelectLabel()}
                disabled={!isNew}
                classNamePrefix={"option"}
                small
              />
            </InlineInputGroup>
          </ResponsiveHalfRowColumn> */}

          <ResponsiveHalfRowColumn>
            <InlineInputGroup
              icon={sheetIcon}
              placeholder={strings(
                "categoriesScreen.fields.weight.placeholder",
              )}
              width={xsmall}
            >
              <InlineNumberInput
                maxLength={8}
                width={full}
                placeholder={strings("categoriesScreen.fields.weight.label")}
                isFormattingDisabled={true}
                {...getNumberFieldProps("weight")}
              />
            </InlineInputGroup>
          </ResponsiveHalfRowColumn>

          <ResponsiveHalfRowColumn>
            <InlineInputGroup
              icon={sheetIcon}
              placeholder={strings("categoriesScreen.fields.tagStatus")}
              width={xsmall}
            >
              <SelectComponent
                {...getSelectFieldProps(
                  "tagStatus",
                  detailCategoryStatusOptions(),
                )}
                placeholder={defaultSelectLabel()}
                classNamePrefix={"menu"}
                small
              />
            </InlineInputGroup>
          </ResponsiveHalfRowColumn>
        </ResponsiveSpreadRow>

        <ResponsiveSpreadRow>
          <ResponsiveHalfRowColumn>
            <InlineInputGroup
              icon={sheetIcon}
              placeholder={strings("categoriesScreen.fields.city.placeholder")}
              width={xsmall}
            >
              <SelectComponent
                {...getSelectFieldProps("city", citiesOptions())}
                placeholder={defaultSelectLabel()}
                classNamePrefix={"menu"}
                small
              />
            </InlineInputGroup>
          </ResponsiveHalfRowColumn>

          <ResponsiveHalfRowColumn>
            <InlineInputGroup
              icon={sheetIcon}
              placeholder={strings(
                "categoriesScreen.fields.screenTarget.placeholder",
              )}
              width={xsmall}
            >
              <SelectComponent
                {...getSelectFieldProps("screenTarget", screenTargetOptions())}
                placeholder={defaultSelectLabel()}
                classNamePrefix={"menu"}
                small
              />
            </InlineInputGroup>
          </ResponsiveHalfRowColumn>
        </ResponsiveSpreadRow>

        {values.screenTarget === ScreenTarget.curatedMap && (
          <ResponsiveSpreadRow>
            <ResponsiveHalfRowColumn />
            <ResponsiveHalfRowColumn>
              <InlineInputGroup
                placeholder={strings(
                  "categoriesScreen.fields.mapSelection.label",
                )}
                width={xsmall}
                offsetContent={<OffsetContentDiv />}
              >
                <MapsSelectList
                  value={values.mapId}
                  fieldName="mapId"
                  onChange={onChange}
                  width="100%"
                  getErrors={getErrors}
                />
              </InlineInputGroup>
            </ResponsiveHalfRowColumn>
          </ResponsiveSpreadRow>
        )}
        {values.screenTarget === ScreenTarget.inAppLink && (
          <ResponsiveSpreadRow>
            <ResponsiveHalfRowColumn />
            <ResponsiveHalfRowColumn>
              <InlineInputGroup
                placeholder={strings("categoriesScreen.fields.inAppLink.label")}
                width={xsmall}
                offsetContent={<OffsetContentDiv />}
              >
                <InAppLinkInput
                  getTextFieldProps={getTextFieldProps}
                  value={
                    values.inAppLink == null
                      ? process.env.REACT_APP_DEEPLINK_PROTOCOL + "://"
                      : values.inAppLink
                  }
                  onChange={onChange}
                  fieldName="inAppLink"
                />
              </InlineInputGroup>
            </ResponsiveHalfRowColumn>
          </ResponsiveSpreadRow>
        )}

        {/*  for Bliggitv2 we dont need it, for now checkbox is commented out and value always set to false */}

        {/* <ResponsiveSpreadRow>
          <ResponsiveHalfRowColumn>
            <InlineInputGroup
              icon={sheetIcon}
              placeholder={strings(
                "categoriesScreen.fields.showAsInterests.label",
              )}
              width={xsmall}
            >
              <Checkbox {...getCheckboxFieldProps("showAsInterests")} />
            </InlineInputGroup>
          </ResponsiveHalfRowColumn>

          <ResponsiveHalfRowColumn>
            {isGeneralType && (
              <InlineInputGroup
                icon={sheetIcon}
                placeholder={strings(
                  "categoriesScreen.fields.showAsDistrict.label",
                )}
                width={xsmall}
              >
                <Checkbox {...getCheckboxFieldProps("showAsDistrict")} />
              </InlineInputGroup>
            )}
          </ResponsiveHalfRowColumn>
        </ResponsiveSpreadRow> */}

        {postSelectedAsCategory && (
          <ResponsiveSpreadRow>
            <ResponsiveHalfRowColumn>
              <InlineInputGroup
                icon={sheetIcon}
                placeholder={strings(
                  "categoriesScreen.fields.showAsSmartService.label",
                )}
                width={xsmall}
              >
                <Checkbox {...showAsSmartServiceCheckBoxFieldProps} />
              </InlineInputGroup>
            </ResponsiveHalfRowColumn>
            <ResponsiveHalfRowColumn>
              <InlineInputGroup
                icon={sheetIcon}
                placeholder={strings(
                  "categoriesScreen.fields.showAsJobOffer.label",
                )}
                width={xsmall}
              >
                <Checkbox {...showAsJobOfferCheckBoxFieldProps} />
              </InlineInputGroup>
            </ResponsiveHalfRowColumn>
          </ResponsiveSpreadRow>
        )}

        <ResponsiveHalfRowColumn>
          {showSmartServiceGroup && (
            <InlineInputGroup
              icon={sheetIcon}
              placeholder={strings(
                "categoriesScreen.fields.smartServiceGroup.placeholder",
              )}
              width={xsmall}
            >
              <SelectComponent
                {...getSelectFieldProps(
                  "smartServiceGroup",
                  getSmartServiceGroupsOptions(),
                )}
                placeholder={defaultSelectLabel()}
                classNamePrefix={"menu"}
                small
              />
            </InlineInputGroup>
          )}
          {isGeneralType && (
            <InlineInputGroup
              icon={gallerieIcon}
              placeholder={strings("categoriesScreen.fields.imageField.label")}
              width={xsmall}
              wrapText={true}
            >
              <AutoDragAndDrop
                {...getMainGeneralCategoryLogoPictureFieldProps("imageUrl")}
                placeholder={strings(`dragAndDrop.dropHere`)}
                container={ImageUploadType.TAGS}
                height="350px"
                width="500px"
                fixedRatio={true}
                slideErrors={slideErrors}
                setSlideErrors={setSlideErrors}
                withCropAndZoom={false}
                ratio={Ratio.ratio16_9}
              />
            </InlineInputGroup>
          )}
        </ResponsiveHalfRowColumn>
      </MarginedArea>
    </>
  );
};

export default MainCategoriesForm;
